import Papa from 'papaparse';
import { v4 as uuidv4 } from 'uuid';
import { ConditionalDefault } from '../models/templating/api/ConditionalDefaultModel';
import TemplateFormFieldOptionModel from '../models/templating/api/TemplateFormFieldOptionModel';

export const TEMPLATE_FORM_CSV_HEADERS = [
    'sectionId', 'sectionTitle', 'sectionPriority', 'fieldId', 'fieldTitle',
    'fieldLabel', 'fieldType', 'associatedVariable', 'placeholder', 'tip', 'defaultValue',
    'conditionalDefaults', 'options', 'isRequired', 'priority'
];

export interface ImportedTemplateFormCsvField {
    fieldId?: string | null;
    title: string;
    label?: string;
    fieldType: string;
    associatedVariable?: string;
    placeholder?: string;
    tip?: string;
    defaultValue?: string;
    conditionalDefaults?: ConditionalDefault;
    options?: TemplateFormFieldOptionModel[];
    isRequired: boolean;
    priority: number;
}

export interface ImportedTemplateFormCsvSection {
    sectionId?: string | null;
    title: string;
    priority: number;
    fields: ImportedTemplateFormCsvField[];
}

export const csvTextToArray = (text: string): string[][] => {
    const parseResult = Papa.parse(text);

    return parseResult.data.map((row: string[]) =>
        row.map((cell: string) => {
            if (typeof cell !== 'string') {
                return cell;
            }

            if (cell.startsWith("'")) {
                const stripped = cell.slice(1);
                const lower = stripped.toLowerCase();
                if (lower === 'true') return 'true';
                if (lower === 'false') return 'false';
                return cell;
            }

            const lower = cell.toLowerCase();
            if (lower === 'true') return 'true';
            if (lower === 'false') return 'false';
            return cell;
        })
    );
};

export const convertToCSVFriendly = (text: string, isRequiredColumn: boolean = false) => {
    if (text == null || text === undefined) return '';

    const textStr = String(text);
    if (!textStr.length) return '';

    const startsWithFormulaChar = /^[-=+@]/.test(textStr);
    const isBooleanLike = /^(true|false)$/i.test(textStr);

    if (isBooleanLike && isRequiredColumn) {
        return `"'${textStr}"`;
    }

    if (textStr.includes(',') || textStr.includes('"') || textStr.includes('\n') || textStr.includes('\r') || startsWithFormulaChar) {
        const escapedText = startsWithFormulaChar ? `'${textStr}` : textStr;
        return `"${escapedText.replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;
    }

    return textStr;
};

export const exportCSV = (data: any[], headers: string[], filename: string) => {
    let csvContent = `${headers.map(header => convertToCSVFriendly(header)).join(',')}\n`;

    data.forEach(item => {
        const row = headers.map(header => {
            const value = item[header];
            return convertToCSVFriendly(value != null ? String(value) : '', header === 'isRequired');
        }).join(',');

        csvContent += row + '\n';
    });

    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
};

export const convertCSVToTemplateSections = (csvArray: string[][]): { sections: ImportedTemplateFormCsvSection[]; errors: string[] } => {
    const errors: string[] = [];

    if (!csvArray || csvArray.length === 0) {
        errors.push('CSV file is empty or invalid. Please select a valid CSV file with template data, or refresh the page to start over.');
        return { sections: [], errors };
    }

    if (csvArray.length < 2) {
        errors.push('CSV file must contain headers and at least one data row. Please check your file format or refresh the page to start over.');
        return { sections: [], errors };
    }

    const headers = csvArray[0];
    const expectedHeaders = TEMPLATE_FORM_CSV_HEADERS;

    if (headers.length < expectedHeaders.length) {
        const missingHeaders = expectedHeaders.slice(headers.length);
        errors.push(`CSV file is missing required columns: ${missingHeaders.join(', ')}. Expected ${expectedHeaders.length} columns, found ${headers.length}. Please update your CSV file with all required columns or refresh the page to start over.`);
    }

    const csvRows = csvArray.slice(1);
    const sectionsMap = new Map<string, ImportedTemplateFormCsvSection>();

    csvRows.forEach((row, rowIndex) => {
        try {
            if (row.length === 0 || row.every(cell => !cell || cell.trim() === '')) {
                return;
            }

            if (row.length < expectedHeaders.length) {
                const missingColumns = expectedHeaders.slice(row.length);
                errors.push(`Row ${rowIndex + 2} is missing required columns: ${missingColumns.join(', ')}. Please complete all required field values before saving or refresh the page to start over.`);
                return;
            }

            const [sectionId, sectionTitle, sectionPriority, fieldId, fieldTitle, fieldLabel, fieldType, associatedVariable, placeholder, tip, defaultValue, conditionalDefaults, options, isRequired, priority] = row;

            if (!sectionTitle || sectionTitle.trim() === '') {
                errors.push(`Row ${rowIndex + 2}: Missing value for "sectionTitle" column. Please complete this required field before saving or refresh the page to start over.`);
                return;
            }

            if (!fieldTitle || fieldTitle.trim() === '') {
                errors.push(`Row ${rowIndex + 2}: Missing value for "fieldTitle" column. Please complete this required field before saving or refresh the page to start over.`);
                return;
            }

            if (!fieldType || fieldType.trim() === '') {
                errors.push(`Row ${rowIndex + 2}: Missing value for "fieldType" column. Please specify a valid field type (text, textarea, select, etc.) before saving or refresh the page to start over.`);
                return;
            }

            const normalizedSectionId = sectionId && sectionId.trim() !== '' && sectionId.toLowerCase() !== 'null'
                ? sectionId.trim()
                : null;
            const sectionKey = normalizedSectionId ?? sectionTitle;

            if (!sectionsMap.has(sectionKey)) {
                sectionsMap.set(sectionKey, {
                    sectionId: normalizedSectionId,
                    title: sectionTitle,
                    priority: parseInt(sectionPriority) || 0,
                    fields: []
                });
            }

            const section = sectionsMap.get(sectionKey);
            if (!section) {
                return;
            }

            let parsedOptions: TemplateFormFieldOptionModel[] = [];
            if (options && options.trim() !== '') {
                try {
                    parsedOptions = JSON.parse(options);
                    if (!Array.isArray(parsedOptions)) {
                        parsedOptions = [];
                    }
                } catch {
                    errors.push(`Row ${rowIndex + 2}: Invalid JSON format in "options" column. Expected format: ["Option 1", "Option 2"]. Please fix this field before saving or refresh the page to start over.`);
                    parsedOptions = [];
                }
            }

            let parsedConditionalDefaults: ConditionalDefault | undefined;
            if (conditionalDefaults && conditionalDefaults.trim() !== '') {
                try {
                    parsedConditionalDefaults = JSON.parse(conditionalDefaults);
                } catch {
                    errors.push(`Row ${rowIndex + 2}: Invalid JSON format in "conditionalDefaults" column. Please fix this field before saving or refresh the page to start over.`);
                }
            }

            section.fields.push({
                fieldId: fieldId && fieldId.trim() !== '' && fieldId.toLowerCase() !== 'null' ? fieldId.trim() : null,
                title: fieldTitle,
                label: fieldLabel,
                fieldType,
                associatedVariable: associatedVariable || '',
                placeholder: placeholder || '',
                tip: tip || '',
                defaultValue: defaultValue || '',
                conditionalDefaults: parsedConditionalDefaults,
                options: parsedOptions,
                isRequired: isRequired === 'true' || isRequired === 'TRUE',
                priority: parseInt(priority) || 0,
            });
        } catch (rowError) {
            errors.push(`Row ${rowIndex + 2}: Error processing data - please check that all required columns have valid values: ${rowError.message}. Complete all required fields before saving or refresh the page to start over.`);
        }
    });

    const sections = Array.from(sectionsMap.values());

    if (sections.length === 0 && errors.length === 0) {
        errors.push('No valid sections found in CSV file. Please check your data format and ensure all required fields are completed, or refresh the page to start over.');
    }

    return { sections, errors };
};

export const sectionsToTemplateForm = (sections: ImportedTemplateFormCsvSection[]) => ({
    templateFormSections: sections.map(section => {
        const sectionId = section.sectionId && section.sectionId.trim() !== '' ? section.sectionId : uuidv4();

        return {
            id: sectionId,
            title: section.title,
            priority: section.priority,
            templateFormFields: section.fields.map(field => ({
                id: field.fieldId && field.fieldId.trim() !== '' ? field.fieldId : uuidv4(),
                templateFormSectionId: sectionId,
                label: field.label,
                fieldType: field.fieldType,
                placeholder: field.placeholder,
                defaultValue: field.defaultValue,
                conditionalDefaults: field.conditionalDefaults,
                tip: field.tip,
                title: field.title,
                isRequired: field.isRequired,
                associatedVariable: field.associatedVariable,
                options: field.options,
                priority: field.priority,
            })),
        };
    })
});
